import React, { useMemo, useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { roleCheck } from "utils/index"
import { Container } from "react-bootstrap"
import FilterDropdown from "components/FilterDropdown"
import SearchInput from "components/SearchInput"
import Loader from "components/Loader"
import AnchorButton from "components/AnchorButton"
import CollapsibleSection from "components/CollapsibleSection"
import { getBaseUrl, getCurrentUser } from "../../reducers";

import {
  REGISTERED_SURVEYORS_MANAGER,
  REGISTERED_SURVEYORS_USER,
  TASK_STATUSES,
  TASK_TYPES
} from "../../../../utils/constants";
import { getRegisterdSurveyors } from "../../../../requesters/userRequester";
import AddTaskModal from "./AddTaskModal";
import { capitalizeString } from "../../../../utils";
import TaskTable from "./TaskTable";
import { getAssignedUsersList, getTasksGroupByAssignedUser } from "../../../../requesters/taskRequester";

const UNASSIGNED_KEY = "Unassigned"

export default function Activities() {
  const taskTypeOptions = useMemo(() => {
    const types = Object.keys(TASK_TYPES)
    return types.map(type => ([capitalizeString(type), TASK_TYPES[type]]))
  }, [])
  const taskStatusOptions = Object.keys(TASK_STATUSES).map(status => ([TASK_STATUSES[status], status]))

  const baseUrl = useSelector(getBaseUrl)
  const currentUser = useSelector(getCurrentUser)
  const systemRole = currentUser.attributes.role
  const currentTeamRole = currentUser.attributes.current_team_role

  // New state for collapsible sections
  const [assignedUsersList, setAssignedUsersList] = useState([])
  const [tasksByUser, setTasksByUser] = useState({})
  const [expandedUsers, setExpandedUsers] = useState({})
  const [loadingUsers, setLoadingUsers] = useState({})
  const [initialLoading, setInitialLoading] = useState(true)

  const [regSurveyorOptions, setRegSurveyorOptions] = useState([])
  const [showAddTaskModal, setShowAddTaskModal] = useState(false)
  const isRSManager = roleCheck(systemRole, currentTeamRole, REGISTERED_SURVEYORS_MANAGER)
  const isRSUser = roleCheck(systemRole, currentTeamRole, REGISTERED_SURVEYORS_USER)
  const canAccess = isRSUser || isRSManager

  if (!canAccess) window.location = "/"

  const initialFilters = {
    by_status: "not_completed",
    by_assigned_surveyor: isRSManager ? undefined : currentUser.id,
    by_task_type: undefined,
    by_keyword: undefined
  }
  const [filters, setFilters] = useState(initialFilters)
  const [dropdownFiltersChanged, setDropdownFiltersChanged] = useState(false)

  const fetchRegSurveyorOptions = () => {
    getRegisterdSurveyors(baseUrl)
      .then(res => {
        if (res.status === 200) {
          const newRegSurveyorOptions = res.response.map(surveyorData => ([`${surveyorData[0]} ${surveyorData[1]}`, surveyorData[2]]))
          setRegSurveyorOptions(newRegSurveyorOptions)
        }
      })
      // eslint-disable-next-line no-console
      .catch(err => console.log(err))
  }

  // Fetch tasks for a specific user (lazy load on expand or refresh)
  const fetchDataForUser = (userKey, userId) => {
    setLoadingUsers(prev => ({ ...prev, [userKey]: true }))

    const userFilters = {
      ...filters,
      by_assigned_surveyor: userId // null for unassigned
    }

    getTasksGroupByAssignedUser(baseUrl, userFilters)
      .then(res => {
        if (res.status === 200) {
          const tasksData = res.response || {}
          // The response is grouped by user name, get the tasks for this specific user
          const userTasks = tasksData[userKey] || []
          setTasksByUser(prev => ({ ...prev, [userKey]: userTasks }))
        }
      })
      // eslint-disable-next-line no-console
      .catch(err => console.log(err))
      .finally(() => setLoadingUsers(prev => ({ ...prev, [userKey]: false })))
  }

  // Fetch lightweight list of users with counts (fast initial load)
  const fetchAssignedUsersList = () => {
    setInitialLoading(true)
    getAssignedUsersList(baseUrl, filters)
      .then(res => {
        if (res.status === 200) {
          const usersList = res.response || []
          setAssignedUsersList(usersList)

          // Auto-expand "Unassigned" section (for managers)
          const unassignedUser = usersList.find(u => u.key === UNASSIGNED_KEY)
          if (unassignedUser && isRSManager) {
            setExpandedUsers(prev => ({ ...prev, [UNASSIGNED_KEY]: true }))
            fetchDataForUser(UNASSIGNED_KEY, null)
          }

          // Auto-expand current user's own section (for all users, especially RS Users)
          const currentUserSection = usersList.find(u => u.id.toString() === currentUser.id)
          if (currentUserSection) {
            setExpandedUsers(prev => ({ ...prev, [currentUserSection.key]: true }))
            fetchDataForUser(currentUserSection.key, currentUserSection.id)
          }
        }
      })
      // eslint-disable-next-line no-console
      .catch(err => console.log(err))
      .finally(() => setInitialLoading(false))
  }

  // Handle toggle expand/collapse
  const handleToggleUser = (userKey, userId) => {
    const isExpanded = expandedUsers[userKey]
    setExpandedUsers(prev => ({ ...prev, [userKey]: !isExpanded }))

    // Fetch data if expanding and data not yet loaded
    if (!isExpanded && !tasksByUser[userKey]) {
      fetchDataForUser(userKey, userId)
    }
  }

  // Handle refresh for a specific user
  const handleRefreshUser = (userKey, userId) => {
    fetchDataForUser(userKey, userId)
  }

  // Refetch user list when filters change
  const fetchData = () => {
    // Reset state when filters change
    setTasksByUser({})
    setExpandedUsers({})
    fetchAssignedUsersList()
  }

  const onSearch = e => {
    e.preventDefault()
    fetchData()
  }

  const onSetFilters = (key, val) => {
    const newFilters = { ...filters }
    newFilters[key] = val
    setFilters(newFilters)
    if (key !== "by_keyword") {
      setDropdownFiltersChanged(true)
    }
  }

  const getFilterName = (key, options) => {
    const selectedOption = options.find(o => o[1] === filters[key])
    const filteredName = selectedOption ? selectedOption[0] : ""

    return filteredName
  }

  useEffect(() => {
    fetchRegSurveyorOptions()
    fetchData()
  }, [])

  useEffect(() => {
    if (dropdownFiltersChanged) {
      fetchData()
      setDropdownFiltersChanged(false)
    }
  }, [dropdownFiltersChanged])

  return (
    <Container fluid className="">
      <div className="container-inner">
        <div className="d-flex justify-content-between align-items-center header-tabs-container">
          <div className="header-tabs">
            <h1>REGISTERED SURVEYORS ACTIVITIES</h1>
          </div>
          <div className="header-buttons">
            { isRSManager && (
              <div className="add-activity">
                <button
                  type="button"
                  className="btn btn-outlined-blue btn-md mr-2"
                  onClick={() => setShowAddTaskModal(true)}>
                  + Add Activity
                </button>
                <AddTaskModal
                  modalTitle="Add Activity"
                  show={showAddTaskModal}
                  onHide={() => setShowAddTaskModal(false)}
                  reloadData={fetchData} />
              </div>
            )}
          </div>
        </div>
        <div className="table-listing-filters d-md-flex justify-content-between">
          <div>
            <div className="btn-group mr-2">
              <SearchInput
                value={filters.by_keyword}
                setValue={val => onSetFilters("by_keyword", val)}
                placeholder="Search by job no, job address"
                handleSubmit={onSearch}
                disableSubmit={initialLoading} />
            </div>
            <div className="btn-group mr-2">
              <FilterDropdown
                title="Status"
                filteredName={getFilterName("by_status", taskStatusOptions)}
                setFilter={val => onSetFilters("by_status", val)}
                options={taskStatusOptions} />
            </div>
            <div className="btn-group mr-2">
              <FilterDropdown
                title="Task Type"
                filteredName={getFilterName("by_task_type", taskTypeOptions)}
                setFilter={val => onSetFilters("by_task_type", val)}
                options={taskTypeOptions} />
            </div>
            { isRSManager && (
              <div className="btn-group mr-2">
                <FilterDropdown
                  title="Assigned To"
                  filteredName={getFilterName("by_assigned_surveyor", regSurveyorOptions)}
                  setFilter={val => onSetFilters("by_assigned_surveyor", val)}
                  options={regSurveyorOptions} />
              </div>
            )}
            <div className="btn-group">
              <AnchorButton
                className="f-small"
                onClick={() => {
                  setFilters(initialFilters)
                  setDropdownFiltersChanged(true)
                }}>
                Clear Filters
              </AnchorButton>
            </div>
          </div>
        </div>
        { initialLoading ? <Loader />
          : (
            <>
              {assignedUsersList.map(user => {
                const userKey = user.key
                const userId = user.id
                const isExpanded = expandedUsers[userKey] || false
                const isLoading = loadingUsers[userKey] || false
                const userTasks = tasksByUser[userKey] || []

                return (
                  <CollapsibleSection
                    key={userKey}
                    name={user.name}
                    count={user.count}
                    isExpanded={isExpanded}
                    isLoading={isLoading}
                    onToggle={() => handleToggleUser(userKey, userId)}
                    onRefresh={() => handleRefreshUser(userKey, userId)}>
                    {userTasks.length > 0 ? (
                      <TaskTable
                        tasks={userTasks}
                        setTasks={newTasks => setTasksByUser(prev => ({ ...prev, [userKey]: newTasks }))}
                        reloadData={() => handleRefreshUser(userKey, userId)} />
                    ) : (
                      !isLoading && <p className="text-muted py-3 px-2">No activities found.</p>
                    )}
                  </CollapsibleSection>
                )
              })}
              {assignedUsersList.length === 0 && (
                <p className="text-muted py-3">No activities found matching your filters.</p>
              )}
            </>
          )}
      </div>
    </Container>
  )
}